/**
 * Tag Selection Section for Recipe Import
 *
 * Displays suggested tags from LLM extraction and allows selection
 * of tags from the available tag library.
 */

import { useState } from 'react';
import type { FoodTag } from '@/types/food';
import { getTagStyle } from '@/types/food';
import { TagSelector } from '@/components/food/TagSelector';

interface TagSectionProps {
  suggestedTags: string[];
  availableTags: FoodTag[];
  selectedTagIds: number[];
  onChange: (tagIds: number[]) => void;
}

/**
 * Chevron icon for collapse toggle
 */
function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

/**
 * Tag icon for section header
 */
function TagIcon() {
  return (
    <svg
      className="w-5 h-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9.568 3H5.25A2.25 2.25 0 003 5.25v4.318c0 .597.237 1.17.659 1.591l9.581 9.581c.699.699 1.78.872 2.607.33a18.095 18.095 0 005.223-5.223c.542-.827.369-1.908-.33-2.607L11.16 3.66A2.25 2.25 0 009.568 3z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 6h.008v.008H6V6z" />
    </svg>
  );
}

export function TagSection({
  suggestedTags,
  availableTags,
  selectedTagIds,
  onChange,
}: TagSectionProps) {
  const [isOpen, setIsOpen] = useState(true);

  // Find matching FoodTag objects for suggested tag names
  const matchedSuggestedTags = suggestedTags
    .map((tagName) => {
      // Try exact match on name
      let tag = availableTags.find((t) => t.name === tagName);

      // Try namespace:value format
      if (!tag && tagName.includes(':')) {
        const [namespace, value] = tagName.split(':');
        tag = availableTags.find(
          (t) => t.namespace === namespace && t.value === value
        );
      }

      return tag;
    })
    .filter((tag): tag is FoodTag => tag !== undefined);

  const toggleSuggestedTag = (tagId: number) => {
    if (selectedTagIds.includes(tagId)) {
      onChange(selectedTagIds.filter((id) => id !== tagId));
    } else {
      onChange([...selectedTagIds, tagId]);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-5 py-4 flex items-center justify-between bg-white hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <TagIcon />
          <div className="text-left">
            <h2 className="text-lg font-semibold text-gray-900">Tags</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {selectedTagIds.length > 0
                ? `${selectedTagIds.length} tag${selectedTagIds.length !== 1 ? 's' : ''} selected`
                : 'Select tags to categorize this recipe'}
            </p>
          </div>
        </div>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {/* Content */}
      {isOpen && (
        <div className="px-5 py-4 border-t border-gray-200 bg-gray-50">
          {/* Suggested Tags */}
          {matchedSuggestedTags.length > 0 && (
            <div className="mb-5">
              <h3 className="text-sm font-medium text-gray-700 mb-3">
                Suggested Tags
              </h3>
              <div className="flex flex-wrap gap-2">
                {matchedSuggestedTags.map((tag) => {
                  const style = getTagStyle(tag.namespace);
                  const isSelected = selectedTagIds.includes(tag.id);

                  return (
                    <button
                      key={tag.id}
                      type="button"
                      onClick={() => toggleSuggestedTag(tag.id)}
                      className={`px-3.5 py-1.5 rounded-full text-sm font-medium transition-all border-2 ${
                        isSelected
                          ? `${style.activeBg} text-white border-transparent`
                          : `${style.bg} ${style.text} border-transparent hover:border-current`
                      }`}
                    >
                      {tag.value}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* All Available Tags */}
          <div>
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              {matchedSuggestedTags.length > 0 ? 'All Tags' : 'Available Tags'}
            </h3>
            <TagSelector
              tags={availableTags}
              selectedTagIds={selectedTagIds}
              onChange={onChange}
            />
          </div>
        </div>
      )}
    </div>
  );
}
